import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Receipt, 
  User, 
  Calendar, 
  CreditCard,
  Printer,
  CheckCircle,
  AlertCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

export default function HotelCheckOut() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [folio, setFolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("Cash");

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await api.get(`/hotel/bookings/${bookingId}`);
      setBooking(response.data.booking);
      setFolio(response.data.booking.folio);
      // Set default payment to balance amount
      setPaymentAmount(response.data.booking.folio?.balance || 0);
    } catch (error) {
      toast.error("Failed to fetch booking details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setProcessing(true);
    try {
      const response = await api.post(`/hotel/bookings/${bookingId}/checkout`, {
        payment_amount: parseFloat(paymentAmount),
        payment_mode: paymentMode
      });

      toast.success("Check-out successful!");
      
      // Print bill
      printBill(response.data.folio);
      
      // Navigate back to bookings
      setTimeout(() => {
        navigate('/hotel-bookings');
      }, 2000);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-out failed");
    } finally {
      setProcessing(false);
    }
  };

  const printBill = (folioData) => {
    // Create printable bill
    const printWindow = window.open('', '_blank');
    const billHtml = generateBillHTML(folioData);
    printWindow.document.write(billHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const generateBillHTML = (folioData) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hotel Bill - ${folioData.folio_number}</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          h1 { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .total { font-weight: bold; font-size: 18px; }
          .text-right { text-align: right; }
        </style>
      </head>
      <body>
        <h1>Hotel Bill</h1>
        <p><strong>Folio Number:</strong> ${folioData.folio_number}</p>
        <p><strong>Guest:</strong> ${folioData.guest_name}</p>
        <p><strong>Room:</strong> ${folioData.room_number}</p>
        <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
        
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Service Type</th>
              <th class="text-right">Amount</th>
              <th class="text-right">GST</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${folioData.transactions?.map(t => `
              <tr>
                <td>${new Date(t.transaction_date).toLocaleDateString()}</td>
                <td>${t.description}</td>
                <td>${t.service_type}</td>
                <td class="text-right">₹${parseFloat(t.amount).toFixed(2)}</td>
                <td class="text-right">₹${parseFloat(t.gst_amount).toFixed(2)}</td>
                <td class="text-right">₹${parseFloat(t.total_amount).toFixed(2)}</td>
              </tr>
            `).join('') || ''}
          </tbody>
          <tfoot>
            <tr class="total">
              <td colspan="5">Subtotal</td>
              <td class="text-right">₹${parseFloat(folioData.total_amount - folioData.total_gst).toFixed(2)}</td>
            </tr>
            <tr class="total">
              <td colspan="5">GST</td>
              <td class="text-right">₹${parseFloat(folioData.total_gst).toFixed(2)}</td>
            </tr>
            <tr class="total">
              <td colspan="5">Grand Total</td>
              <td class="text-right">₹${parseFloat(folioData.total_amount).toFixed(2)}</td>
            </tr>
            <tr>
              <td colspan="5">Paid</td>
              <td class="text-right">₹${parseFloat(folioData.total_paid).toFixed(2)}</td>
            </tr>
            <tr class="total">
              <td colspan="5">Balance</td>
              <td class="text-right">₹${parseFloat(folioData.balance).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
        
        <p style="text-align: center; margin-top: 40px;">Thank you for staying with us!</p>
      </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!booking || !folio) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Booking or folio not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const totalPaid = parseFloat(folio.total_paid || 0);
  const paymentEntered = parseFloat(paymentAmount || 0);
  const finalBalance = parseFloat(folio.balance || 0) - paymentEntered;

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2D2C2A]">
          Check-Out - Room {booking.room_number}
        </h1>
        <p className="text-sm text-[#7A7873] mt-1">
          Complete payment and finalize guest departure
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Guest & Booking Info */}
        <div className="space-y-4">
          <Card className="p-6">
            <h2 className="font-semibold text-lg flex items-center gap-2 mb-4">
              <User size={20} className="text-[#D4AF37]" />
              Guest Information
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Name:</span>
                <span className="font-semibold">{booking.guest_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Phone:</span>
                <span className="font-semibold">{booking.guest_phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Booking Code:</span>
                <span className="font-semibold">{booking.booking_code}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="font-semibold text-lg flex items-center gap-2 mb-4">
              <Calendar size={20} className="text-[#D4AF37]" />
              Stay Details
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Check-In:</span>
                <span className="font-semibold">{booking.check_in_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Check-Out:</span>
                <span className="font-semibold">{booking.check_out_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Total Nights:</span>
                <span className="font-semibold">{booking.total_nights}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7873]">Plan Type:</span>
                <span className="font-semibold">{booking.plan_type}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: Folio & Payment */}
        <div className="space-y-4">
          <Card className="p-6">
            <h2 className="font-semibold text-lg flex items-center gap-2 mb-4">
              <Receipt size={20} className="text-[#D4AF37]" />
              Folio Details
            </h2>
            
            {/* Transactions */}
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {folio.transactions?.map((trans, idx) => (
                <div key={idx} className="flex justify-between text-sm py-2 border-b">
                  <div>
                    <div className="font-medium">{trans.description}</div>
                    <div className="text-xs text-[#7A7873]">
                      {trans.service_type}
                      {trans.reference_type === 'order' && trans.reference_id && (
                        <span className="ml-2 text-blue-600">• Order #{trans.reference_id}</span>
                      )}
                    </div>
                    <div className="text-xs text-[#7A7873]">
                      {new Date(trans.transaction_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">₹{parseFloat(trans.total_amount).toFixed(2)}</div>
                    {trans.service_type === 'Restaurant' && (
                      <div className="text-xs text-blue-600">Room Service</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-2 pt-4 border-t-2">
              <div className="flex justify-between text-sm">
                <span>Room Charges:</span>
                <span className="font-semibold">₹{parseFloat(folio.total_room_charges || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Restaurant/Services:</span>
                <span className="font-semibold">₹{parseFloat(folio.total_service_charges || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>GST:</span>
                <span className="font-semibold">₹{parseFloat(folio.total_gst || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Grand Total:</span>
                <span className="text-[#D4AF37]">₹{parseFloat(folio.total_amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Already Paid:</span>
                <span className="font-semibold">₹{totalPaid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg text-red-600">
                <span>Balance Due:</span>
                <span>₹{parseFloat(folio.balance).toFixed(2)}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="font-semibold text-lg flex items-center gap-2 mb-4">
              <CreditCard size={20} className="text-[#D4AF37]" />
              Final Payment
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="payment_amount">Payment Amount</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="payment_mode">Payment Mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Online">Online Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {paymentEntered > 0 && (
                <div className="bg-blue-50 p-3 rounded">
                  <div className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span>Current Balance:</span>
                      <span className="font-semibold">₹{parseFloat(folio.balance).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span>Payment Now:</span>
                      <span className="font-semibold">₹{paymentEntered.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Remaining:</span>
                      <span className={finalBalance > 0 ? "text-red-600" : "text-green-600"}>
                        ₹{finalBalance.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/hotel-bookings')}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028]"
                  onClick={handleCheckOut}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Complete Check-Out
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
